import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Card, PillButton, Input, Modal } from '../../ui';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash.svg';
import './AdminLayout.css';
import { apiErrorMessage } from '../../utils/apiError';

function Roles() {
  const { t } = useTranslation();
  const { has, orgId } = useOutletContext();
  const canManage = has('roles.manage');

  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState('');
  const [formPermissions, setFormPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/org/${orgId}/roles`)
      .then((res) => {
        setRoles(res.data.roles || []);
        setAvailablePermissions(res.data.availablePermissions || []);
      })
      .catch((err) => setError(apiErrorMessage(err, t, 'errors.generic')))
      .finally(() => setLoading(false));
  }, [orgId, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setFormName(''); setFormPermissions([]); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); setFormName(r.name); setFormPermissions(r.permissions || []); setModalOpen(true); };

  const togglePermission = (perm) => {
    setFormPermissions((prev) => (prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await api.patch(`/org/${orgId}/roles/${editing.id}`, { name: formName, permissions: formPermissions });
      } else {
        await api.post(`/org/${orgId}/roles`, { name: formName, permissions: formPermissions });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, t, 'errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(t('admin.roles.confirmDelete'))) return;
    try {
      await api.delete(`/org/${orgId}/roles/${r.id}`);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, t, 'errors.generic'));
    }
  };

  if (loading) return <p>{t('common.loading')}</p>;

  return (
    <div>
      <div className="sgc-admin__section-header">
        <h2 className="sgc-admin__section-title">{t('admin.tabs.roles')}</h2>
        {canManage && <PillButton icon={PlusIcon} onClick={openCreate}>{t('admin.roles.add')}</PillButton>}
      </div>

      {error && <p className="sgc-admin__error">{error}</p>}

      <Card padded={false}>
        <table>
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('admin.roles.permissionsCount')}</th>
              <th>{t('common.type')}</th>
              {canManage && <th>{t('common.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.name}</strong></td>
                <td className="sgc-admin__muted">{r.permissions.length} / {availablePermissions.length}</td>
                <td>
                  {r.isSystem
                    ? <span className="sgc-admin__badge">{t('admin.roles.system')}</span>
                    : <span className="sgc-admin__badge sgc-admin__badge--success">{t('admin.roles.custom')}</span>}
                </td>
                {canManage && (
                  <td>
                    <div className="sgc-admin__row-actions">
                      <button
                        type="button" className="sgc-admin__icon-btn" title={t('common.edit')}
                        disabled={r.isSystem} onClick={() => openEdit(r)}
                      >
                        <EditIcon width={16} height={16} aria-hidden="true" />
                      </button>
                      <button
                        type="button" className="sgc-admin__icon-btn sgc-admin__icon-btn--danger" title={t('common.delete')}
                        disabled={r.isSystem} onClick={() => handleDelete(r)}
                      >
                        <TrashIcon width={16} height={16} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('common.edit') : t('admin.roles.add')}>
        <form className="sgc-admin__form" onSubmit={handleSave}>
          <Input label={t('common.name')} value={formName} onChange={(e) => setFormName(e.target.value)} required />
          <div className="sgc-admin__checkbox-grid">
            {availablePermissions.map((perm) => (
              <label key={perm} className="sgc-admin__checkbox">
                <input
                  type="checkbox"
                  checked={formPermissions.includes(perm)}
                  onChange={() => togglePermission(perm)}
                />
                {perm}
              </label>
            ))}
          </div>
          <div className="sgc-admin__form-actions">
            <PillButton type="submit" disabled={saving || !formName.trim()}>
              {saving ? t('common.loading') : t('common.save')}
            </PillButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Roles;
